// Gate shown when the visitor is outside Telegram and has no session.
//
// Primary path \u2014 bot deep-link auth ("phoneless"):
//
//   1. Click "Open in Telegram"           \u2192 POST /auth/bot/start.
//   2. Open tg://resolve?...&start=auth_T \u2192 user's Telegram app pops up.
//      Fallback to t.me/<bot>?start=auth_T after ~700ms in case there's
//      no native client (Telegram Web handles the t.me link).
//   3. User taps Start in the bot         \u2192 webhook claims the token.
//   4. Poll GET /auth/bot/check?token=T   \u2192 server sets cookie when ready,
//      we reload the SPA.
//
// Fallback path \u2014 the classic Login Widget in redirect mode (data-auth-url
// avoids the eval() that breaks under our CSP). Shown behind a smaller
// "use phone number instead" link for users without a Telegram client.

import { createSignal, onMount, onCleanup, Show } from "solid-js";
import { api } from "../lib/api";
import { t } from "../lib/i18n";

interface Props {
  botUsername: string;
  onAuthed: () => void;
}

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_MS = 5 * 60 * 1000; // matches token TTL on the worker

type Mode = "bot" | "widget";
type BotStatus = "idle" | "waiting" | "ready" | "expired" | "error";

export function TelegramLoginGate(props: Props) {
  // Surface a server-side auth error (?auth_error=...) from the widget
  // redirect path, otherwise undefined.
  const initialAuthError = (() => {
    try {
      return new URL(window.location.href).searchParams.get("auth_error");
    } catch {
      return null;
    }
  })();

  const [mode, setMode] = createSignal<Mode>("bot");
  const [botStatus, setBotStatus] = createSignal<BotStatus>("idle");
  const [errorMsg, setErrorMsg] = createSignal<string | null>(initialAuthError);
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let pollDeadline = 0;
  let widgetMountRef: HTMLDivElement | undefined;

  const stopPolling = () => {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = undefined;
    }
  };

  // When the user comes back from Telegram, fire an immediate poll,
  // typically the token is already claimed by then, but we don't want
  // them sitting through up to a full POLL_INTERVAL_MS of stale UI.
  let currentPoll: (() => Promise<void>) | undefined;
  const onVisibility = () => {
    if (!document.hidden && currentPoll && botStatus() === "waiting") {
      stopPolling();
      void currentPoll();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  onCleanup(() => {
    stopPolling();
    document.removeEventListener("visibilitychange", onVisibility);
  });

  const beginBotLogin = async () => {
    setBotStatus("waiting");
    setErrorMsg(null);
    try {
      const { token, deep_link, web_link, ttl_seconds } = await api.botLoginStart();
      pollDeadline = Date.now() + Math.min(ttl_seconds * 1000, POLL_MAX_MS);

      // Try the native deep link first via an invisible anchor click,
      // works around browsers that block `window.location.href` for
      // non-http protocols and avoids the chance of navigating the SPA
      // tab away (which would kill polling). 700 ms later we open the
      // universal t.me link in a new tab; Telegram Web handles it for
      // users without a native client.
      const a = document.createElement("a");
      a.href = deep_link;
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => {
        if (!document.hidden) {
          window.open(web_link, "_blank", "noopener,noreferrer");
        }
      }, 700);

      const poll = async () => {
        if (Date.now() > pollDeadline) {
          setBotStatus("expired");
          return;
        }
        try {
          const res = await api.botLoginCheck(token);
          if (res.status === "ready") {
            setBotStatus("ready");
            // Cookie is set on .saaslots.com; full reload picks it up so
            // every cached resource re-fetches with auth.
            window.location.reload();
            return;
          }
          if (res.status === "expired") {
            setBotStatus("expired");
            return;
          }
        } catch {
          // Network blip, keep polling until the deadline.
        }
        pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      };
      currentPoll = poll;
      pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (e: unknown) {
      const code = (e as { body?: { error?: string } })?.body?.error ?? "start_failed";
      setBotStatus("error");
      setErrorMsg(code);
    }
  };

  const switchToWidget = () => {
    stopPolling();
    setMode("widget");
  };

  // Lazily mount the Login Widget iframe only after the user opts into
  // the phone path \u2014 saves a third-party script + iframe on the default
  // bot-flow render and avoids COOP/CSP surprises for users who never
  // need it.
  onMount(() => {
    /* widget script is injected by the widgetEffect below */
  });

  // Reactive effect: when mode flips to widget, inject the script tag
  // into the placeholder div. Solid's `Show` unmounts on flip-back so we
  // don't need to clean up explicitly.
  const widgetEffect = (el: HTMLDivElement | null) => {
    if (!el || el.dataset.mounted === "1") return;
    el.dataset.mounted = "1";
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", props.botUsername);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-radius", "12");
    script.setAttribute("data-auth-url", `${API_BASE || ""}/auth/telegram/callback`);
    script.setAttribute("data-request-access", "write");
    el.appendChild(script);
  };

  void props.onAuthed; // parent reloads on success; nothing to call here.
  void widgetMountRef; // ref kept for symmetry with future tweaks.

  return (
    <div class="login-gate">
      <div class="login-card">
        <div class="login-title">{t("login.title")}</div>
        <div class="login-body">{t("login.body")}</div>

        <Show
          when={mode() === "bot"}
          fallback={
            <div class="login-widget-wrap">
              <div ref={widgetEffect} class="login-widget" aria-live="polite" />
              <button type="button" class="login-tertiary" onClick={() => setMode("bot")}>
                {t("login.backToBot")}
              </button>
            </div>
          }
        >
          <Show
            when={botStatus() === "waiting"}
            fallback={
              <button
                type="button"
                class="login-primary"
                onClick={beginBotLogin}
                disabled={botStatus() === "ready"}
              >
                <span class="login-primary-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                  </svg>
                </span>
                <span>{t("login.openInTelegram")}</span>
              </button>
            }
          >
            <div class="login-waiting">
              <div class="login-status">{t("login.waitingForTap")}</div>
              <button type="button" class="login-tertiary" onClick={beginBotLogin}>
                {t("login.resend")}
              </button>
            </div>
          </Show>

          <Show when={botStatus() === "expired"}>
            <div class="login-error" role="alert">
              {t("login.expired")}
            </div>
          </Show>

          <button type="button" class="login-secondary" onClick={switchToWidget}>
            {t("login.usePhone")}
          </button>
        </Show>

        <Show when={errorMsg() && botStatus() !== "waiting"}>
          <div class="login-error" role="alert">
            {t("login.error")}: {errorMsg()}
          </div>
        </Show>

        <button
          type="button"
          class="login-demo-link"
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("demo", "1");
            window.location.href = url.toString();
          }}
        >
          {t("login.tryDemo")}
        </button>
      </div>
    </div>
  );
}
